/**
 * Shared content and types across all discharge templates (dental, neutering, surgery).
 */
import { SNOOTS_HQ, VETERIS } from '@snootsvet/shared'

/** A medication entry in a discharge form's medication list. */
export interface DischargeMedicationEntry {
  key: string
  productId: string
  name: string
  /** Dosage or administration instructions — label varies by template. */
  instructions: string
}

export const THINGS_TO_NOTICE = [
  'If your pet has had pre-anaesthetic blood performed today, you may notice a small clip patch on their neck. This hair will grow back in 6-8 weeks.',
  "There will be a small clip patch on your pet's forearm, this is where the IV catheter was placed. Some bruising may occur here but this should subside within a couple of days. If your pet has been sent home with a compression bandage in this area, please remove this when you get home. This compression bandage is designed to stick to itself, not your pets fur or skin so can be removed with ease.",
  'You may notice clear discharge from your pets eyes, this is where lubricating eye gel was applied throughout the procedure and will clear on its own.',
  'Your pet may develop a cough or croak post procedure. This is from the use of an endotracheal tube providing your pet with anesthetic gasses and oxygen throughout the procedure. This should subside after 24-48 hours post procedure. Please get in contact with the clinic if this persists.',
]

export const OUT_OF_HOURS = `We are here to answer any questions or concerns you may have. Please do not hesitate to contact us. If we are not open, our out of hours emergency contact is ${VETERIS.name} on ${VETERIS.phone}.`

export function closingText(patientName: string) {
  return `Thank you for trusting us with ${patientName}'s care today!`
}

/** Dental-specific closing text variant. */
export function dentalClosingText(patientName: string) {
  return `Thank you for trusting us with ${patientName}'s teeth today!`
}

export const MEDICATION_SIDE_EFFECTS =
  'Monitor for side effects such as vomiting, diarrhoea or reduced appetite. Please contact the clinic if any of these occur.'

export function medicationText(patientName: string) {
  return `${patientName} has received adequate pain relief medication today which should last 24 hours. If you are concerned about your pets wellbeing, please do not hesitate to contact the clinic.`
}

export function restText(patientName: string) {
  return `Following ${patientName}'s procedure today, expect them to feel a bit drowsy for the next 24-48 hours while the medications given today have time to fully metabolise. Allow ${patientName} to rest in a quiet and comfortable area away from other pets for the next 12 hours.`
}

export function activityText(name: string): string {
  return `Please keep ${name} indoors or closely confined for the next 7–10 days so the incision can stay clean and dry. No jumping, running, or rough play for 10–14 days. Lead walks only are recommended. After 10–14 days, your pet can return to normal activity. Excessive activity often leads to re-injury or serious complications that may necessitate re-operation. This means additional expense to you and added discomfort to your pet.`
}

export function anaesthesiaText(name: string): string {
  return `When ${name} is discharged, they may still be groggy from the anaesthesia. They may appear depressed, wobbly and may tremble. ${name} should be allowed to recover in a quiet room at home away from other pets. Check on their progress intermittently.`
}

export function foodWaterText(name: string): string {
  return `First offer only small amounts of water, and if ${name} seems alert and hungry, offer small amounts of food (~ 1/3 of usual meal). Every pet recovers differently. Some pets may not want anything at all the first night. If your pet vomits, withhold food until the next morning. Please let us know immediately vomiting persists. By tomorrow ${name} should be alert and have a normal appetite & can then be fed normally.`
}

export const BATHING_TEXT =
  'Do not bathe your pet for 10 days (until the incision is healed). The incision should stay clean and dry.'

export function monitoringText(name: string): string {
  return `${name} must wear the buster collar for the full 10 days unless instructed otherwise by a vet. Check the incision daily for signs of excessive redness, swelling, or discharge. If any of these are noted, please contact us immediately.`
}

export function painMedIntroText(name: string): string {
  return `${name} is going home with some pain medication to start tomorrow:`
}

export const FOLLOW_UP_WHATSAPP = `All follow up appointments are done via our remote team on Whatsapp; please message us on ${SNOOTS_HQ.phone}`

export function woundPhotoText(name: string): string {
  return `Please send us an update & a photo of ${name}'s wound on:`
}
